import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { MAUZAS } from '../data/mock';

const Row = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 py-1.5 border-b border-border/60 last:border-b-0">
    <div className="text-xs uppercase tracking-wide text-muted-foreground col-span-1">{label}</div>
    <div className="text-sm text-foreground col-span-2 break-words">{value || <span className="text-muted-foreground/60">—</span>}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-5">
    <div className="font-serif text-base font-semibold text-primary mb-2">{title}</div>
    <div className="rounded-lg border border-border bg-card px-4 py-2">{children}</div>
  </div>
);

const ApplicationDetailsDialog = ({ app, open, onOpenChange }) => {
  if (!app) return null;
  const fd = app.formDetails || {};
  const isNoc = app.type === 'NOC_TRADE';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-xs">{app.id}</Badge>
            <span>{app.typeLabel}</span>
          </DialogTitle>
          <DialogDescription>
            {app.applicantName} · {app.akhing} · {MAUZAS.find((m) => m.id === app.mauzaNumber)?.label}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[65vh] px-6 py-4">
          <Section title="Application">
            <Row label="Type" value={app.typeLabel} />
            <Row label="A’khing" value={app.akhing} />
            <Row label="Mauza" value={MAUZAS.find((m) => m.id === app.mauzaNumber)?.label} />
            {app.purpose && <Row label="Purpose" value={app.purpose} />}
            {app.days && <Row label="Validity" value={`${app.days} days`} />}
            <Row label="Submitted" value={(app.createdAt || '').slice(0, 10)} />
            {app.approvalCode && <Row label="Approval Code" value={app.approvalCode} />}
          </Section>

          {isNoc && Object.keys(fd).length > 0 && (
            <>
              <Section title="Applicant">
                <Row label="1. Name" value={fd.applicantName} />
                <Row label="2. Father’s Name" value={fd.fatherName} />
                <Row label="3. Phone / WhatsApp" value={fd.phone} />
                <Row label="10. Aadhar No." value={fd.aadharNumber} />
                <Row label="10. Election ID" value={fd.voterIdNumber} />
              </Section>

              <Section title="4. Applicant’s Address">
                <Row label="Address" value={fd.applicantAddress} />
                <Row label="P.S." value={fd.applicantPS} />
                <Row label="State" value={fd.applicantState} />
              </Section>

              <Section title="5. Present Address">
                <Row label="Address" value={fd.presentAddress} />
                <Row label="P.S." value={fd.presentPS} />
                <Row label="State" value={fd.presentState} />
              </Section>

              <Section title="6. Nature of Business">
                <Row label="Type" value={fd.natureOfBusiness} />
                <Row label="9. Place of Work / Trade" value={fd.placeOfWork} />
              </Section>

              <Section title="7-8. Responsible Person">
                <Row label="Name" value={fd.responsiblePersonName} />
                <Row label="Address" value={fd.responsiblePersonAddress} />
                <Row label="Phone" value={fd.responsiblePersonPhone} />
              </Section>
            </>
          )}

          {app.type === 'LAND' && Object.keys(fd).length > 0 && (
            <>
              <Section title="Land Details">
                <Row label="A&rsquo;khing / Village" value={fd.akhing || app.akhing} />
                <Row label="Land Document Number" value={fd.documentNumber} />
                <Row label="Total Area" value={fd.totalArea} />
              </Section>
              <Section title="Boundaries">
                <Row label="North" value={fd.boundaryNorth} />
                <Row label="South" value={fd.boundarySouth} />
                <Row label="East" value={fd.boundaryEast} />
                <Row label="West" value={fd.boundaryWest} />
              </Section>
            </>
          )}

          <Section title="Timeline">
            <div className="space-y-2 py-1">
              {(app.timeline || []).map((t, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="h-2 w-2 rounded-full bg-primary" />
                  <span className="font-medium capitalize">{t.stage.replace(/_/g, ' ')}</span>
                  <span className="text-muted-foreground">{(t.at || '').slice(0, 16).replace('T', ' ')}</span>
                  <span className="text-muted-foreground">· {t.by}</span>
                </div>
              ))}
            </div>
          </Section>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationDetailsDialog;
